// -----------------------------------------------------------------------------
//  ZapFeed.js (complete with hyperlinking on header and avatar)
//  – adds iOS APNs-token debug card + automatic resend after login
//  – hyperlinks creators to /creator/{creator_id} and users to /user/{user_id}
//  – links are now on display name (header) and avatar, not in description
//  – FIXED: Creator posts now properly link to /creators/ profile
//  – FIXED: Mobile text overflow issue in composer
// -----------------------------------------------------------------------------

import React, { useRef, useEffect, useState } from "react";
import {
  Box, Typography, Avatar, List, ListItem, ListItemAvatar, Paper,
  IconButton, Skeleton, Link, TextField, Button, Snackbar, Alert,
  useTheme, alpha,
} from "@mui/material";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import { useInfiniteQuery, useMutation, useQueryClient } from "react-query";
import {
  MonetizationOn as PayoutIcon,
  Campaign as CampaignIcon,
  Handshake as PartnershipIcon,
  Send as SendIcon,
  PersonAdd as JoinIcon,
  Telegram as PostIcon,
  People as UserIcon,
  Payments as PaymentIcon,
} from "@mui/icons-material";
import { Capacitor }   from "@capacitor/core";
import { Preferences } from "@capacitor/preferences";

import API                from "../../../../API";
import { useCreatorAuth } from "../../../../Hooks/creator-use-auth";
import useAuth            from "../../../../Hooks/use-auth";

/* ──────────────────────────────────────────────────────────────── */
const ROW_COUNT = 20;

const PLACEHOLDERS = [
  "https://images.unsplash.com/photo-1509822929063-6b6cfc9b42f2",
  "https://images.unsplash.com/photo-1517841905240-472988babdf9",
  "https://images.unsplash.com/photo-1502685104226-ee32379fefbe",
  "https://images.unsplash.com/photo-1529626455594-4ff0802cfb7e",
];

const activityIcons = {
  Payout:      <PayoutIcon fontSize="small" />,
  Campaign:    <CampaignIcon fontSize="small" />,
  Partnership: <PartnershipIcon fontSize="small" />,
  Pitch:       <SendIcon fontSize="small" />,
  Join:        <JoinIcon fontSize="small" />,
  Post:        <PostIcon fontSize="small" />,
  User:        <UserIcon fontSize="small" />,
  Payment:     <PaymentIcon fontSize="small" />,
};

/* ───────────────── activity summary ───────────────────────────── */
function ActivitySummary({ activity }) {
  const { activity_type, activity_date, activity_name, creator_id, user_id } = activity;
  const type   = activity_type?.trim();
  const date   = new Date(activity_date).toLocaleDateString("en-US", { month: "short", day: "numeric" });

  const phrases = {
    Post:        activity_name,
    Campaign:    `started ${activity_name}`,
    Pitch:       `pitched for ${activity_name}`,
    Payout:      "received a payout",
    Partnership: "started a partnership",
    Join:        "joined Blitz",
    User:        "just joined Blitz",
    Payment:     "sent a payment",
  };
  const description = phrases[type] ?? activity_name;

  // Helper function to create the appropriate link for the display name
  const createDisplayNameLink = (displayName) => {
    // Check for creator ID (could be in creator_id or username field)
    const creatorIdentifier = creator_id || activity.username || activity.creator_user?.username;
    
    if (creatorIdentifier) {
      return (
        <Link
          component={RouterLink}
          to={`/creators/${creatorIdentifier}`}
          sx={{ 
            fontWeight: 600, 
            textDecoration: "none", 
            color: "primary.main",
            "&:hover": { textDecoration: "underline" } 
          }}
        >
          {displayName}
        </Link>
      );
    } else if (user_id) {
      return (
        <Link
          component={RouterLink}
          to={`/user/${user_id}`}
          sx={{ 
            fontWeight: 600, 
            textDecoration: "none", 
            color: "primary.main",
            "&:hover": { textDecoration: "underline" } 
          }}
        >
          {displayName}
        </Link>
      );
    } else if (type === "Post" && displayName && displayName !== "A New User") {
      // For posts without clear creator_id/user_id, default to creator profile
      // Extract a reasonable identifier from display name or use as-is
      const creatorSlug = displayName.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
      return (
        <Link
          component={RouterLink}
          to={`/creators/${creatorSlug}`}
          sx={{ 
            fontWeight: 600, 
            textDecoration: "none", 
            color: "primary.main",
            "&:hover": { textDecoration: "underline" } 
          }}
        >
          {displayName}
        </Link>
      );
    } else {
      // No link, just display the text with styling
      return (
        <Typography component="span" sx={{ fontWeight: 600 }}>
          {displayName}
        </Typography>
      );
    }
  };

  const displayName = activity.display_name || creator_id || activity.username || "A New User";

  return (
    <Box>
      <Typography component="div" fontWeight={600}>
        {createDisplayNameLink(displayName)}
      </Typography>
      <Typography 
        variant="body1" 
        sx={{ 
          whiteSpace: "pre-wrap",
          wordWrap: "break-word",
          overflowWrap: "break-word",
          maxWidth: "100%"
        }}
      >
        {description}
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={0.5}>{date}</Typography>
    </Box>
  );
}

/* ───────────────── avatar with link ───────────────────────────── */
function LinkedAvatar({ activity, ...avatarProps }) {
  const { creator_id, user_id, pfphref, display_name, activity_type } = activity;
  
  const avatar = (
    <Avatar
      src={pfphref || PLACEHOLDERS[1]}
      alt={creator_id || display_name || "avatar"}
      {...avatarProps}
    />
  );

  // Check for creator ID (could be in creator_id or username field)
  const creatorIdentifier = creator_id || activity.username || activity.creator_user?.username;

  if (creatorIdentifier) {
    return (
      <Link
        component={RouterLink}
        to={`/creators/${creatorIdentifier}`}
        sx={{ textDecoration: "none" }}
      >
        {avatar}
      </Link>
    );
  } else if (user_id) {
    return (
      <Link
        component={RouterLink}
        to={`/user/${user_id}`}
        sx={{ textDecoration: "none" }}
      >
        {avatar}
      </Link>
    );
  } else if (activity_type === "Post" && display_name && display_name !== "A New User") {
    // For posts without clear creator_id/user_id, default to creator profile
    const creatorSlug = display_name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
    return (
      <Link
        component={RouterLink}
        to={`/creators/${creatorSlug}`}
        sx={{ textDecoration: "none" }}
      >
        {avatar}
      </Link>
    );
  } else {
    return avatar;
  }
}

/* ───────────────── main component ─────────────────────────────── */
export default function ZapFeed() {
  const theme     = useTheme();
  const navigate  = useNavigate();
  const qc        = useQueryClient();

  /* auth flags */
  const { creatorToken, isAuthorized: creatorOK } = useCreatorAuth();
  const { isAuthorized: userOK,   getCurrrentUser } = useAuth();

  /* iOS token capture -------------------------------------------------- */
  const isiOS = Capacitor.getPlatform() === "ios";
  const [apnsToken, setApnsToken] = useState(null);

  // 1) gather token from window + Preferences
  useEffect(() => {
    if (!isiOS) return;

    if (window._pendingApnsToken) setApnsToken(window._pendingApnsToken);
    Preferences.get({ key: "apns_token" }).then(({ value }) => value && setApnsToken(value));
  }, [isiOS]);

  // 2) resend when logged-in
  useEffect(() => {
    if (!isiOS || !apnsToken) return;
    if (!userOK && !creatorOK)   return;

    API.notifications
      .registerDeviceToken({ device_token: apnsToken, platform: "ios" })
      .then(() => { window._pendingApnsToken = null; })
      .catch(console.error);
  }, [isiOS, apnsToken, userOK, creatorOK]);

  /* identity & avatars ------------------------------------------------- */
  const isCreator = !!creatorToken?.creator_user;
  let displayName = "Me";
  let photo       = PLACEHOLDERS[Math.floor(Math.random() * PLACEHOLDERS.length)];

  if (isCreator) {
    const c  = creatorToken.creator_user;
    displayName = c.creator?.display_name || c.username || displayName;
    photo       = c.creator?.pfphref     || c.pfphref   || photo;
  } else if (userOK) {
    const u = getCurrrentUser();
    displayName = u?.first_name || u?.last_name
      ? `${u.first_name ?? ""} ${u.last_name ?? ""}`.trim()
      : u?.email || u?.username || displayName;
    photo = u?.phphref || photo;
  }

  /* local state -------------------------------------------------------- */
  const [text,  setText]  = useState("");
  const [snack, setSnack] = useState(null);      // {msg, sev}

  /* feed query --------------------------------------------------------- */
  const {
    data, isLoading, isFetchingNextPage, fetchNextPage,
    hasNextPage, error, refetch,
  } = useInfiniteQuery(
    ["zapfeed4"],
    ({ pageParam = 0 }) => API.zapfeed.list({ page: pageParam, rows: ROW_COUNT }),
    {
      getNextPageParam: (last, pages) =>
        last.zapfeed.length === ROW_COUNT ? pages.length : undefined,
      staleTime: 3_600_000,
      refetchOnWindowFocus: false,
    }
  );

  useEffect(() => {
    if (userOK || creatorToken) refetch();
  }, [userOK, creatorToken, refetch]);

  /* infinite-scroll sentinel ------------------------------------------ */
  const sentinelRef = useRef(null);
  useEffect(() => {
    if (!sentinelRef.current || !hasNextPage) return;
    const obs = new IntersectionObserver(
      ([e]) => e.isIntersecting && fetchNextPage(),
      { rootMargin: "400px 0px 0px 0px" }
    );
    obs.observe(sentinelRef.current);
    return () => obs.disconnect();
  }, [fetchNextPage, hasNextPage]);

  /* create-post mutation ---------------------------------------------- */
  const createPost = useMutation(API.zapfeed.post, {
    onSuccess: ({ post }) => {
      const newItem = {
        activity_id:   post.id,
        activity_name: post.text,
        activity_type: "Post",
        activity_date: post.created_at,
        creator_id:    isCreator ? creatorToken.creator_user.username : null,
        user_id:       !isCreator && userOK ? getCurrrentUser()?.id : null,
        pfphref:       photo,
        display_name:  displayName,
      };
      qc.setQueryData(["zapfeed4"], (old) => {
        if (!old) return old;
        const first  = old.pages[0];
        const first2 = { ...first, zapfeed: [newItem, ...first.zapfeed] };
        return { ...old, pages: [first2, ...old.pages.slice(1)] };
      });
      setText(""); setSnack({ msg: "Posted!", sev: "success" });
    },
    onError: (err) => {
      const msg = err?.response?.data?.error || "Something went wrong.";
      setSnack({ msg: msg.includes("posted") ? "You can only post once per day." : msg, sev: "warning" });
    },
  });

  const handleSubmit = () => {
    const t = text.trim();
    if (!t || t.length > 280 || createPost.isLoading) return;
    createPost.mutate({ text: t });
  };

  const handlePitchClick = (a) => {
    if (!isCreator) return;
    const id = a?.activity_id;
    if (!id) return;
    if (["Campaign", "Pitch"].includes(a.activity_type))      navigate(`/creatorconnect/pitch/campaign/${id}`);
    else if (a.activity_type === "Partnership")               navigate(`/creatorconnect/pitch/partnership/${id}`);
  };

  const feed = data?.pages.flatMap((p) => p.zapfeed) ?? [];

  /* render ------------------------------------------------------------- */
  return (
    <Box sx={{ maxWidth: 800, mx: "auto", px: { xs: 1, sm: 2 } }}>      

      {/* composer */}
      <Paper
        elevation={0}
        sx={{
          p: 2, mb: 3, borderRadius: 3,
          border: `1px solid ${theme.palette.divider}`,
          bgcolor: alpha(theme.palette.primary.light, 0.04),
        }}
      >
        <Box display="flex" gap={2}>
          <Avatar src={photo} alt={displayName} sx={{ width: 48, height: 48, flexShrink: 0 }} />
          <Box flex={1} sx={{ minWidth: 0, width: "100%" }}>
            <TextField
              multiline minRows={2} maxRows={6} fullWidth
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Share something with your network…"
              variant="standard" 
              InputProps={{ 
                disableUnderline: true,
                sx: {
                  wordWrap: "break-word",
                  overflowWrap: "break-word",
                  whiteSpace: "pre-wrap",
                  overflow: "hidden",
                  "& .MuiInputBase-input": {
                    wordWrap: "break-word",
                    overflowWrap: "break-word",
                    whiteSpace: "pre-wrap",
                    overflow: "hidden",
                  }
                }
              }}
              sx={{
                "& .MuiInput-root": {
                  wordWrap: "break-word",
                  overflowWrap: "break-word",
                  whiteSpace: "pre-wrap",
                  overflow: "hidden",
                },
                "& textarea": {
                  wordWrap: "break-word !important",
                  overflowWrap: "break-word !important",
                  whiteSpace: "pre-wrap !important",
                  overflow: "hidden !important",
                  resize: "none",
                }
              }}
            />
            <Box display="flex" justifyContent="space-between" mt={1} sx={{ minWidth: 0 }}>
              <Typography
                variant="caption"
                color={text.length > 280 ? "error.main" : "text.secondary"}
                sx={{ flexShrink: 0 }}
              >
                {text.length}/280
              </Typography>
              <Button
                size="small" variant="contained"
                disabled={!text.trim() || text.trim().length > 280 || createPost.isLoading}
                onClick={handleSubmit}
                sx={{ flexShrink: 0, ml: 1 }}
              >
                Post
              </Button>
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* feed list */}
      <List sx={{ p: 0 }}>
        {feed.map((act) => {
          const icon = activityIcons[act.activity_type] || null;
          return (
            <Paper
              key={`${act.activity_type}-${act.activity_id ?? act.creator_id ?? act.user_id ?? act.activity_date}`}
              elevation={0}
              sx={{
                mb: 2, p: 2, borderRadius: 3,
                border: `1px solid ${theme.palette.divider}`,
                transition: "all .2s ease",
                "&:hover": { boxShadow: theme.shadows[3], transform: "translateY(-2px)" },
                overflow: "hidden",
                wordWrap: "break-word",
              }}
            >
              <ListItem
                disableGutters alignItems="center"
                sx={{ 
                  pr: { xs: 10, sm: 11 },
                  minWidth: 0,
                  overflow: "hidden"
                }}
                secondaryAction={
                  <Box display="flex" alignItems="center" gap={1.5}>
                    {icon && (
                      <Box
                        sx={{
                          width: 32, height: 32, borderRadius: "50%",
                          bgcolor: alpha(theme.palette.primary.main, 0.12),
                          color: theme.palette.primary.main,
                          display: "flex", alignItems: "center", justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        {icon}
                      </Box>
                    )}
                    {isCreator && ["Campaign", "Partnership", "Pitch"].includes(act.activity_type) && (
                      <IconButton
                        onClick={() => handlePitchClick(act)}
                        sx={{ 
                          color: theme.palette.primary.main, 
                          "&:hover": { transform: "scale(1.2)" },
                          flexShrink: 0,
                        }}
                      >
                        <SendIcon />
                      </IconButton>
                    )}
                  </Box>
                }
              >
                <ListItemAvatar sx={{ minWidth: 0, mr: 2, flexShrink: 0 }}>
                  <LinkedAvatar
                    activity={act}
                    sx={{
                      width: 56, height: 56,
                      border: `2px solid ${theme.palette.background.paper}`,
                      boxShadow: theme.shadows[1],
                    }}
                  />
                </ListItemAvatar>
                <Box sx={{ minWidth: 0, flex: 1, overflow: "hidden" }}>
                  <ActivitySummary activity={act} />
                </Box>
              </ListItem>
            </Paper>
          );
        })}

        {/* skeletons while loading */}
        {(isLoading || isFetchingNextPage) &&
          Array.from({ length: ROW_COUNT }).map((_, i) => (
            <Paper key={`sk-${i}`} sx={{ mb: 2, p: 2, borderRadius: 3, border: `1px solid ${theme.palette.divider}` }}>
              <ListItem disableGutters alignItems="center">
                <ListItemAvatar sx={{ minWidth: 0, mr: 2 }}>
                  <Skeleton variant="circular" width={56} height={56} />
                </ListItemAvatar>
                <Box flex={1}>
                  <Skeleton variant="text" width="35%" height={24} />
                  <Skeleton variant="text" width="60%" height={20} />
                </Box>
              </ListItem>
            </Paper>
          ))}

        <Box ref={sentinelRef} sx={{ height: 1 }} />

        {error && (
          <Paper sx={{ p: 3, textAlign: "center", bgcolor: theme.palette.error.light, borderRadius: 3 }}>
            <Typography color="error">Failed to load activities. Please refresh the page.</Typography>
          </Paper>
        )}
      </List>

      {/* snackbar */}
      <Snackbar
        open={!!snack}
        autoHideDuration={4000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {snack && (
          <Alert
            severity={snack.sev}
            variant="filled"
            onClose={() => setSnack(null)}
            sx={{ width: "100%" }}
          >
            {snack.msg}
          </Alert>
        )}
      </Snackbar>
    </Box>
  );
}